import { HI_STATUS_COLOR } from '@/entities/monitoring/hi';
import type { ColorSchemeType } from '@/shared/lib/hooks/use-app-color-scheme';
import { getClampRadius } from '@/shared/lib/utils/clamp-mantine-radius';

import {
  ActionBar,
  Affix,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  darken,
  DEFAULT_THEME,
  defaultVariantColorsResolver,
  Fieldset,
  Indicator,
  lighten,
  type MantineColorShade,
  type MantineRadius,
  type MantineTheme,
  type MantineThemeOverride,
  Menu,
  Modal,
  MultiSelect,
  Paper,
  parseThemeColor,
  Pill,
  PillGroup,
  PillsInput,
  Popover,
  Portal,
  Radio,
  rem,
  rgba,
  ScrollArea,
  ScrollAreaAutosize,
  SegmentedControl,
  Select,
  Splitter,
  Tabs,
  TextInput,
  Tooltip,
  TreeSelect,
} from '@mantine/core';
import { DatePicker, DatePickerInput, DateTimePicker } from '@mantine/dates';
import '@mantine/dropzone/styles.css';
import cs from 'clsx';
import { useTranslation } from 'react-i18next';
import { createWithEqualityFn as create } from 'zustand/traditional';
import { persist } from 'zustand/middleware';

const MODAL_TRANSITION_MS = 200;

const customTransitions = {
  popTop: {
    in: { opacity: 1, transform: 'scaleY(1) translateY(0)' },
    out: { opacity: 0, transform: 'scaleY(0.9) translateY(-3px)' },
    common: { transformOrigin: 'top' },
    transitionProperty: 'opacity, transform',
  },
};
const variantColorResolver = (colorScheme: ColorSchemeType) => (input) => {
  const defaultResolvedColors = defaultVariantColorsResolver(input);
  const parsedColor = parseThemeColor({
    color: input.color || input.theme.primaryColor,
    theme: input.theme,
  });

  const isDark = colorScheme === 'dark';
  // Completely override variant
  // change later to light-outlined
  if (input.variant === 'outline') {
    return {
      background: rgba(parsedColor.value, 0.2),
      hover: rgba(parsedColor.value, 0.25),
      border: `${rem(1)} solid ${lighten(parsedColor.value, 0.3)}`,
      color: isDark
        ? lighten(parsedColor.value, 0.5)
        : darken(parsedColor.value, 0.2),
    };
  }

  return defaultResolvedColors;
};

interface ThemeStore {
  primaryShade: MantineColorShade;
  setPrimaryShade: (shade: MantineColorShade) => void;
  defaultRadius: string;
  setDefaultRadius: (radius: string) => void;
  primaryColor: string;
  setPrimaryColor: (color: string) => void;
}

export const useThemeStore = create(
  persist<ThemeStore>(
    (set) => ({
      primaryShade: 6,
      setPrimaryShade: (shade) => set({ primaryShade: shade }),
      defaultRadius: 'md',
      setDefaultRadius: (radius) => set({ defaultRadius: radius }),
      primaryColor: DEFAULT_THEME.primaryColor,
      setPrimaryColor: (color) => set({ primaryColor: color }),
    }),
    {
      name: 'theme-store',
    },
  ),
);
const ComboboxNothingFoundMessage = () => {
  const { t } = useTranslation();

  return (
    <div data-testid='mantine-combobox-nothing-found'>
      {t('text.nothingFound')}
    </div>
  );
};
export const mantineTheme = ({
  defaultRadius = 'md',
  primaryColor = 'blue',
  primaryShade = 6,
  colorScheme,
}: {
  defaultRadius?: MantineRadius;
  primaryColor?: string;
  primaryShade?: MantineColorShade;
  colorScheme: ColorSchemeType;
}): MantineThemeOverride => ({
  variantColorResolver: variantColorResolver(colorScheme),
  defaultRadius,
  primaryShade,
  cursorType: 'pointer' as MantineTheme['cursorType'],
  other: {
    transitions: customTransitions,
    modalTransitionDuration: MODAL_TRANSITION_MS,

    // radius: {
    //   xs: '4px !important',
    //   sm: '6px !important',
    //   md: '8px !important',
    //   lg: '12px !important',
    // },
  },
  components: {
    Select: Select.extend({
      defaultProps: {
        limit: 100,
        searchable: true,
        nothingFoundMessage: <ComboboxNothingFoundMessage />,
      },
    }),
    Checkbox: Checkbox.extend({ defaultProps: { variant: 'light' } }),
    CheckboxIndicator: Checkbox.Indicator.extend({
      defaultProps: { variant: 'light' },
    }),
    Radio: Radio.extend({ defaultProps: { variant: 'light' } }),
    MultiSelect: MultiSelect.extend({
      defaultProps: {
        limit: 100,
        searchable: true,
        nothingFoundMessage: <ComboboxNothingFoundMessage />,
      },
    }),
    TreeSelect: TreeSelect.extend({
      defaultProps: {
        nothingFoundMessage: <ComboboxNothingFoundMessage />,
      },
    }),
    Card: Card.extend({
      classNames: {
        root: cs(Paper.classes.root, '!bg-[var(--mantine-color-body)]'),
      },
    }),

    PillsInput: PillsInput.extend({
      defaultProps: {
        py: 0,
        classNames: {
          input: '!flex !flex-col',
        },
      },
    }),
    PillGroup: PillGroup.extend({
      defaultProps: {
        h: '100%',
        style: { flexGrow: 1 },
      },
    }),
    DatePicker: DatePicker.extend({
      defaultProps: {
        highlightToday: true,
      },
    }),
    DatePickerInput: DatePickerInput.extend({
      defaultProps: {
        valueFormat: 'DD.MM.YYYY',
        highlightToday: true,
      },
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        autoComplete: 'off',
        attributes: {
          root: {
            'data-lpignore': 'true',
          },
        },
      },
    }),
    DateTimePicker: DateTimePicker.extend({
      defaultProps: {
        attributes: {
          calendarHeaderLevel: {
            'data-testid': 'mantine-calendar-header',
          },
          calendarHeaderControl: {
            'data-testid': 'mantine-calendar-header-control',
          },
          monthCell: {
            'data-testid': 'mantine-day-cell',
          },
          monthsListCell: {
            'data-testid': 'mantine-months-cell',
          },
          submitButton: {
            'data-testid': 'mantine-submit-button',
          },
        },
        valueFormat: 'DD.MM.YYYY HH:mm:ss',
        highlightToday: true,
      },
    }),
    Tabs: Tabs.extend({
      vars: (_, props) => ({
        root: {
          '--tabs-radius': `var(--mantine-radius-${getClampRadius(defaultRadius, 'xs', props.orientation !== 'vertical' ? 'lg' : 'xl')})`,
        },
      }),
      classNames: (_, { orientation }) => {
        return {
          tab: cs('transition-all duration-150', {
            'bg-transparent': orientation !== 'vertical',
          }),
          tabLabel: 'opacity-85',
          list: 'relative',
        };
      },
    }),
    Splitter: Splitter.extend({
      defaultProps: { lineSize: 0 },
    }),
    Pill: Pill.extend({
      classNames: {
        label: 'font-medium',
        // root: 'dark:!bg-dark-6',
      },
    }),
    Fieldset: Fieldset.extend({
      defaultProps: {
        m: 0,
        radius: getClampRadius(defaultRadius, 'xs', 'lg'),
      },
    }),

    Button: Button.extend({
      defaultProps: {
        variant: 'default',
      },
    }),
    Indicator: Indicator.extend({
      // Shrink root to children so badge sits on the target (ActionIcon), not a stretched cell.
      defaultProps: {
        size: 8,
        inline: true,
      },
    }),

    Modal: Modal.extend({
      classNames: (_, { filled }) => {
        if (filled) {
          return {
            root: '!flex !flex-col',
            inner: 'grow overflow-hidden !flex !flex-col',
            content: 'grow w-full !flex !flex-col',
            body: '!flex !flex-col grow overflow-hidden',
          };
        }
        return {};
      },
      defaultProps: {
        transitionProps: {
          duration: MODAL_TRANSITION_MS,
          transition: 'fade-down',
        },
        scrollAreaComponent: ScrollAreaAutosize.withProps({
          scrollbars: 'y',
          classNames: { content: '!min-w-full' },
        }),
      },
    }),
    ScrollArea: ScrollArea.extend({
      classNames: {
        scrollbar: '!bg-transparent',
      },
      defaultProps: {
        // offsetScrollbars: true
        // type: 'scroll',
      },
    }),
    ScrollAreaAutoSize: ScrollAreaAutosize.extend({
      defaultProps: {
        mah: '90vh',
      },
    }),
    Tooltip: Tooltip.extend({
      defaultProps: {
        withArrow: true,
      },
    }),
    Popover: Popover.extend({
      defaultProps: {
        radius: getClampRadius(defaultRadius, 'xs', 'lg'),
        shadow: 'lg',
        withArrow: true,
        arrowPosition: 'center',
      },
      // classNames:{
      //   dropdown: '!bg-[var(--mantine-color-body)]'
      // }
      // classNames: () => {
      //   return {
      //     dropdown: `p-3 !text-sm`,
      //   };
      // },
    }),
    ColorInput: {
      defaultProps: {
        swatches: Object.keys(HI_STATUS_COLOR).map(
          (key) => HI_STATUS_COLOR[key],
        ),
      },
    },
    Portal: Portal.extend({
      defaultProps: {
        reuseTargetNode: true,
      },
    }),
    Center: Center.extend({
      classNames: { root: 'h-full' },
    }),
    SegmentedControl: SegmentedControl.extend({
      defaultProps: {
        // radius: useThemeStore.getState().defaultRadius,
        withItemsBorders: false,
      },
    }),
    Paper: Paper.extend({
      defaultProps: {
        radius: getClampRadius(defaultRadius, 'xs', 'lg'),
      },
    }),
    Affix: Affix.extend({
      classNames: {
        root: '[&.width-before-scroll-bar]:pointer-events-none',
      },
    }),
    ActionBar: ActionBar.extend({
      classNames: {
        root: 'pointer-events-auto',
      },
    }),

    Alert: Alert.extend({
      defaultProps: {
        radius: getClampRadius(defaultRadius, 'xs', 'lg'),
      },
    }),
    Badge: Badge.extend({
      defaultProps: {
        radius: defaultRadius,
        variant: 'outline',
      },
    }),
    Chip: Chip.extend({
      defaultProps: {
        radius: defaultRadius,
      },
    }),
    Menu: Menu.extend({
      defaultProps: {
        transitionProps: {
          // transition: customTransitions.popTop,
          // timingFunction: 'ease',
        },
      },
      // classNames: {
      //   dropdown: '!p-[6px]',
      //   dropdown: '!p-[6px]',
      //   divider: '!my-1',
      //   // item: '!rounded-[6px]',
      // },
    }),
  },
  primaryColor: DEFAULT_THEME.colors[primaryColor] ? primaryColor : 'blue',
});

// export const theme = mergeMantineTheme(DEFAULT_THEME, mantineTheme);